/*
 * Designer Rugs — global behaviours (vanilla, progressive enhancement).
 * Custom elements re-initialise naturally on shopify:section:load because
 * connectedCallback runs whenever the editor re-injects a section.
 */

/**/
/* Focus trap (lean take on Dawn's pattern)*/
/**/
const FOCUSABLE = 'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), input:not([type=hidden]):enabled, select:enabled, textarea:enabled, iframe';
const trapHandlers = {};

function trapFocus(container, elementToFocus = container) {
  removeTrapFocus();
  const focusable = Array.from(container.querySelectorAll(FOCUSABLE));
  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  trapHandlers.keydown = (event) => {
    if (event.code !== 'Tab') return;
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus({ preventScroll: true });
    }
  };
  trapHandlers.focusout = () => document.removeEventListener('keydown', trapHandlers.keydown);
  trapHandlers.focusin = (event) => {
    if (event.target !== container && !container.contains(event.target)) return first.focus({ preventScroll: true });
    document.addEventListener('keydown', trapHandlers.keydown);
  };

  document.addEventListener('focusout', trapHandlers.focusout);
  document.addEventListener('focusin', trapHandlers.focusin);
  (elementToFocus || first).focus({ preventScroll: true });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapHandlers.focusin);
  document.removeEventListener('focusout', trapHandlers.focusout);
  document.removeEventListener('keydown', trapHandlers.keydown);
  if (elementToFocus) elementToFocus.focus({ preventScroll: true });
}

window.trapFocus = trapFocus;
window.removeTrapFocus = removeTrapFocus;

/* Custom elements can connect mid-parse, before their children exist.
   Run init only once the document (or injected fragment) is complete.*/
function whenParsed(fn) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fn, { once: true });
  } else {
    fn();
  }
}

/**/
/* Sticky chrome scroll watcher: hides/reveals announcement bar +*/
/* Header as one unit. Stamps data-chrome-hidden on <html> and*/
/* Publishes --chrome-height / --announcement-height.*/
/**/
class StickyChrome extends HTMLElement {
  connectedCallback() {
    whenParsed(() => this.init());
  }

  init() {
    this.lastY = window.scrollY;
    this.onScroll = this.onScroll || this.update.bind(this);
    this.onSectionLoad = this.onSectionLoad || this.observeSections.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    document.addEventListener('shopify:section:load', this.onSectionLoad);
    this.observeSections();
  }

  observeSections() {
    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => this.publishHeights());
    this.sections().forEach((s) => this.resizeObserver.observe(s));
    this.publishHeights();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('shopify:section:load', this.onSectionLoad);
    this.resizeObserver?.disconnect();
  }

  sections() {
    return ['.announcement-bar', '.site-header']
      .map((sel) => document.querySelector(sel)?.closest('.shopify-section'))
      .filter(Boolean);
  }

  publishHeights() {
    const [bar, header] = ['.announcement-bar', '.site-header'].map((sel) => document.querySelector(sel));
    const barH = bar ? bar.getBoundingClientRect().height : 0;
    const headerH = header ? header.getBoundingClientRect().height : 0;
    const root = document.documentElement;
    root.style.setProperty('--announcement-height', `${barH}px`);
    root.style.setProperty('--chrome-height', `${barH + headerH}px`);
    /* The HEADER row on its own. Full-bleed heroes run up behind the
       transparent nav but must NOT cover the announcement bar, so they need
       this height alone — --chrome-height would take them to the very top of
       the viewport. Published here rather than measured per section so every
       hero uses one number and it stays right through the hide/reveal.*/
    root.style.setProperty('--header-height', `${headerH}px`);
  }

  update() {
    const y = window.scrollY;
    const root = document.documentElement;
    /* 🔴 data-chrome-lock FREEZES the hide/reveal for the length
       of a programmatic scroll. The guide anchor bar sets it while it jumps
       to a section and clears it on arrival.

       Why it is needed: the bar's sticky `top` rides --chrome-height, so the
       bar MOVES whenever this watcher flips. An anchor jump scrolls down,
       which hides the chrome and lifts the bar by a header height — and the
       landing correction then scrolls UP a few pixels to compensate, which
       this watcher reads as a scroll-up and SHOWS the chrome again, dropping
       the bar back down. Measured live: offsets oscillating between +76 and
       −32 across three guide pages, never settling. Freezing the chrome
       makes the bar's position constant for the duration, so the landing is
       arithmetic instead of a race.
       lastY is kept current while locked, so unlocking never sees one large
       synthetic delta and mistakes it for a gesture.*/
    if (root.dataset.chromeLock !== undefined) { this.lastY = y; return; }
    const goingDown = y > this.lastY;
    if (y < 80) {
      delete root.dataset.chromeHidden;
    } else if (goingDown && y - this.lastY > 2) {
      // never hide while a menu inside the chrome is open
      if (!document.querySelector('.site-header [aria-expanded="true"]')) {
        root.dataset.chromeHidden = '';
      }
    } else if (!goingDown && this.lastY - y > 2) {
      delete root.dataset.chromeHidden;
    }
    this.lastY = y;
  }
}
customElements.define('sticky-chrome', StickyChrome);

/**/
/* Mega navigation: hover intent + keyboard focus open, close delay,*/
/* ESC closes, focus-out closes. Panels live inside each nav item.*/
/**/
class MegaNav extends HTMLElement {
  connectedCallback() {
    whenParsed(() => this.init());
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;
    this.items = Array.from(this.querySelectorAll('[data-mega-item]'));
    this.timers = new Map();
    this.items.forEach((item) => {
      const trigger = item.querySelector('[aria-controls]');
      const panel = item.querySelector('.mega');
      if (!trigger || !panel) return;
      this.timers.set(item, {});
      item.addEventListener('mouseenter', () => {
        const t = this.timers.get(item);
        clearTimeout(t.close);
        t.open = setTimeout(() => this.open(item), 60);
      });
      item.addEventListener('mouseleave', () => {
        const t = this.timers.get(item);
        clearTimeout(t.open);
        t.close = setTimeout(() => this.close(item), 220);
      });
      trigger.addEventListener('focus', () => this.open(item));
      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) this.close(item);
      });
      trigger.addEventListener('keydown', (event) => {
        if (event.code === 'ArrowDown' && trigger.getAttribute('aria-expanded') === 'true') {
          event.preventDefault();
          panel.querySelector('a, button')?.focus();
        }
      });
    });
    this.onKeyup = (event) => {
      if (event.code !== 'Escape') return;
      const openItem = this.items.find((i) => i.querySelector('[aria-expanded="true"]'));
      if (openItem) {
        this.close(openItem);
        openItem.querySelector('[aria-controls]')?.focus();
      }
    };
    this.addEventListener('keyup', this.onKeyup);
  }

  open(item) {
    const t = this.timers.get(item);
    if (t) clearTimeout(t.close);
    this.items.forEach((other) => {
      if (other !== item) this.close(other);
    });
    item.querySelector('[aria-controls]')?.setAttribute('aria-expanded', 'true');
    const panel = item.querySelector('.mega');
    if (panel) panel.hidden = false;
  }

  close(item) {
    item.querySelector('[aria-controls]')?.setAttribute('aria-expanded', 'false');
    const panel = item.querySelector('.mega');
    if (panel) panel.hidden = true;
  }
}
customElements.define('mega-nav', MegaNav);

/**/
/* Mobile nav drawer: slide-in dialog with accordions, focus-trapped.*/
/**/
class NavDrawer extends HTMLElement {
  connectedCallback() {
    whenParsed(() => this.init());
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;
    this.toggle = this.querySelector('[data-drawer-toggle]');
    this.drawer = this.querySelector('[data-drawer]');
    this.overlay = this.querySelector('[data-drawer-overlay]');
    if (!this.toggle || !this.drawer) return;
    this.toggle.addEventListener('click', () => (this.isOpen() ? this.close() : this.open()));
    this.drawer.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') this.close();
    });
    this.drawer.querySelector('[data-drawer-close]')?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());
    this.initDrill();
  }

  /* 20: 3-level drill-down. Panels are absolutely stacked; the active chain
     is a stack of panel ids — the top panel slides in from the right,
     ancestors sit offscreen left (is-parent). Back pops. Off-stack panels
     are aria-hidden + inert so the focus trap can never tab offscreen.
     Focus: drilling focuses the new panel's Back; Back returns focus to the
     drill control that opened the panel. Reduced motion: CSS drops the
     transition (snap).*/
  initDrill() {
    this.panels = new Map();
    this.drawer.querySelectorAll('[data-drill-panel]').forEach((panel) => {
      this.panels.set(panel.dataset.drillPanel, panel);
    });
    if (!this.panels.has('root')) return;
    this.stack = ['root'];
    this.openers = new Map();
    this.drawer.addEventListener('click', (event) => {
      const to = event.target.closest('[data-drill-to]');
      if (to) {
        const id = to.dataset.drillTo;
        if (this.panels.has(id)) {
          this.openers.set(id, to);
          this.stack.push(id);
          this.renderDrill();
          /* PreventScroll is load-bearing, not tidiness. The drill
             viewport's scrollWidth is twice its clientWidth (the offscreen
             panels sit at translate:100%), so focusing a control inside the
             INCOMING panel made the browser scroll that box sideways to
             reveal it — dragging the active panel off the left edge and
             leaving the client with an apparently empty sub-panel. The CSS
             now clips rather than scrolls, which fixes it structurally;
             this stops us asking for the scroll in the first place.*/
          this.panels.get(id).querySelector('[data-drill-back]')?.focus({ preventScroll: true });
        }
        return;
      }
      const back = event.target.closest('[data-drill-back]');
      if (back && this.stack.length > 1) {
        const popped = this.stack.pop();
        this.renderDrill();
        this.openers.get(popped)?.focus({ preventScroll: true });
      }
    });
  }

  renderDrill() {
    const top = this.stack[this.stack.length - 1];
    this.panels.forEach((panel, id) => {
      const inStack = this.stack.includes(id);
      panel.classList.toggle('is-active', id === top);
      panel.classList.toggle('is-parent', inStack && id !== top);
      const off = id !== top;
      panel.toggleAttribute('inert', off);
      if (off) panel.setAttribute('aria-hidden', 'true');
      else panel.removeAttribute('aria-hidden');
      const opener = this.openers.get(id);
      if (opener) opener.setAttribute('aria-expanded', inStack ? 'true' : 'false');
    });
  }

  isOpen() {
    return this.toggle.getAttribute('aria-expanded') === 'true';
  }

  open() {
    this.toggle.setAttribute('aria-expanded', 'true');
    if (this.stack) {
      this.stack = ['root'];
      this.renderDrill();
    }
    this.drawer.hidden = false;
    if (this.overlay) this.overlay.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    window.trapFocus(this.drawer, this.drawer.querySelector('[data-drawer-close]') || this.drawer);
  }

  close() {
    this.toggle.setAttribute('aria-expanded', 'false');
    this.drawer.hidden = true;
    if (this.overlay) this.overlay.hidden = true;
    document.documentElement.style.overflow = '';
    window.removeTrapFocus(this.toggle);
  }
}
customElements.define('nav-drawer', NavDrawer);

/**/
/* Cart drawer: opens from the bag, re-renders itself via the cart*/
/* AJAX API's sections parameter (Section Rendering API), announces*/
/* Changes on an aria-live region, traps focus while open.*/
/**/
class CartDrawer extends HTMLElement {
  connectedCallback() {
    whenParsed(() => this.init());
  }

  init() {
    this.dialog = this.querySelector('#CartDrawer');
    this.overlay = this.querySelector('[data-cart-overlay]');
    if (!this.dialog) return;

    this.onTriggerClick = this.onTriggerClick || ((event) => {
      const trigger = event.target.closest('[data-cart-drawer-trigger]');
      if (!trigger) return;
      event.preventDefault();
      this.open(trigger);
    });
    document.addEventListener('click', this.onTriggerClick);

    if (this.localBound) return;
    this.localBound = true;
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-close]') || event.target === this.overlay) this.close();
      const qtyBtn = event.target.closest('[data-cart-qty]');
      if (qtyBtn) this.change(qtyBtn.dataset.line, qtyBtn.dataset.cartQty);
    });
    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') this.close();
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onTriggerClick);
  }

  open(triggeredBy) {
    window.DRTradeEnquiry?.renderDrawer();
    this.activeTrigger = triggeredBy || this.activeTrigger;
    this.overlay.hidden = false;
    this.dialog.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    window.trapFocus(this.dialog, this.dialog.querySelector('[data-cart-close]'));
  }

  close() {
    this.overlay.hidden = true;
    this.dialog.hidden = true;
    document.documentElement.style.overflow = '';
    window.removeTrapFocus(this.activeTrigger || null);
  }

  announce(kind) {
    const template = this.querySelector(`[data-cart-${kind}-template]`);
    const live = this.querySelector('[data-cart-live]');
    if (live) live.textContent = template ? template.textContent : '';
  }

  async change(line, quantity) {
    if (this.pending) return;
    this.pending = true;
    this.dialog.setAttribute('aria-busy', 'true');
    const qtyButtons = Array.from(this.querySelectorAll('[data-cart-qty]'));
    qtyButtons.forEach((btn) => (btn.disabled = true));
    try {
      const changeUrl = this.dataset.changeUrl || '/cart/change';
      const response = await fetch(`${changeUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity: Number(quantity), sections: this.dataset.sectionId }),
      });
      const state = await response.json();
      if (!response.ok || state.status) {
        this.announce('error');
        return;
      }
      const html = state.sections?.[this.dataset.sectionId];
      if (html) this.renderContents(html);
      this.syncCount(state.item_count);
      this.announce('live');
      document.dispatchEvent(new CustomEvent('cart:change', { detail: state }));
    } catch (e) {
      this.announce('error');
    } finally {
      this.pending = false;
      this.dialog.removeAttribute('aria-busy');
      qtyButtons.forEach((btn) => (btn.disabled = false));
    }
  }

  syncCount(count) {
    if (typeof count !== 'number') return;
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
    document.querySelectorAll('[data-cart-count-label]').forEach((el) => {
      el.textContent = el.textContent.replace(/\d+/, count);
    });
  }

  renderContents(html) {
    if (window.DRTradeEnquiry?.active) {
      window.DRTradeEnquiry.renderDrawer();
      return;
    }
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const fresh = parsed.querySelector('#CartDrawer');
    if (!fresh) return;
    const wasHidden = this.dialog.hidden;
    this.dialog.innerHTML = fresh.innerHTML;
    this.dialog.hidden = wasHidden;
    if (!wasHidden) {
      // the node the user activated is gone — restore focus inside the
      // modal and rebind the trap to the fresh first/last nodes
      window.trapFocus(this.dialog, this.dialog);
    }
  }
}
customElements.define('cart-drawer', CartDrawer);

/**/
/* Unit preference (Metric | Imperial): one shared preference written*/
/* Written by the footer control and read by the PDP toggle — one shared
   unit preference across both surfaces.*/
/* LocalStorage 'dr:units' + a 'units:change' document event.*/
/**/
const UNITS_KEY = 'dr:units';

class UnitToggle extends HTMLElement {
  connectedCallback() {
    whenParsed(() => this.init());
  }

  init() {
    this.onExternalChange = this.onExternalChange || ((event) => {
      if (event.target !== this) this.reflect(event.detail.unit);
    });
    document.addEventListener('units:change', this.onExternalChange);
    if (this.initialized) return;
    this.initialized = true;
    this.buttons = Array.from(this.querySelectorAll('[data-unit]'));
    let stored = null;
    try {
      stored = localStorage.getItem(UNITS_KEY);
    } catch (e) {
      /* Storage unavailable — session-only state*/
    }
    this.reflect(stored || this.dataset.default || 'metric');
    this.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-unit]');
      if (btn) this.set(btn.dataset.unit);
    });
  }

  disconnectedCallback() {
    document.removeEventListener('units:change', this.onExternalChange);
  }

  set(unit) {
    try {
      localStorage.setItem(UNITS_KEY, unit);
    } catch (e) {
      /* Storage unavailable*/
    }
    this.reflect(unit);
    document.dispatchEvent(new CustomEvent('units:change', { detail: { unit } }));
  }

  reflect(unit) {
    this.buttons?.forEach((btn) => btn.setAttribute('aria-pressed', String(btn.dataset.unit === unit)));
    document.documentElement.dataset.units = unit;
  }
}
customElements.define('unit-toggle', UnitToggle);
/* Size-label conversion itself lives in product.js (convertSizeLabel —
   hardened item 6): ProductPage.applyUnits runs it on units:change,
   on init (stored preference), and again automatically after every
   colourway/construction swap because the swapped-in product-page
   element re-initialises. Do NOT re-declare a converter here — classic
   scripts share one global scope and the later declaration silently
   wins (caught live: a global.js duplicate shadowed product.js's).*/

/**/
/* Announcement USP rotation + rollover descriptions*/
/**/
/* WCAG 2.2.2: A PERSISTENT PAUSE FOR AUTO-MOVING*/
/* CONTENT. Two things on this site start moving by themselves and run*/
/* Longer than 5s: the Home hero (autoplay ON at 5s by*/
/* ) and the announcement bar's USP rotation. Both paused on*/
/* Hover/focus, which is NOT a 2.2.2 mechanism — a keyboard or*/
/* Screen-reader user who never hovers had no way to stop either.*/
/*                                                                      */
/* ONE PREFERENCE, TWO SURFACES — the same pattern the metric/imperial*/
/* Toggle already uses. Pausing either rotator pauses both, and it*/
/* Is honoured for the visit via sessionStorage, so a reader is not*/
/* Asked to re-pause on every page. sessionStorage, not localStorage:*/
/* This is a reading accommodation for the current visit, not a stored*/
/* Profile, and it must not silently disable motion forever on a shared*/
/* Machine.*/
/*                                                                      */
/* ⚠ Home is -LOCKED. The control is deliberately minimal and sits*/
/* INSIDE furniture that already exists (the hero's dash row), so no*/
/* New element is introduced to a signed-off page. Placement is flagged*/
/* To here.*/
/* ⚠ It composes with prefers-reduced-motion rather than replacing it:*/
/* Reduced motion still stops autoplay outright, and the control then*/
/* Has nothing to pause.*/
/**/
/* 🔴 THE PAUSE/PLAY TOGGLES ARE REMOVED (Chris's ruling, 01.09), and with
   them the shared preference that backed them — the sessionStorage key, the
   rotators-paused event and the binding helper. Nothing else read them.

   ⚠ THIS IS AN ACCEPTED NON-COMPLIANCE, NOT AN OVERSIGHT. It overrides Codex
   P1-08 / WCAG 2.2.2 on BOTH auto-rotating surfaces — the announcement
   rotator and the hero slideshow. The remaining mitigations are the hover,
   focus and reduced-motion pauses immediately below, and they are asserted by
   verify-p205-nopause.mjs. Do not restore the buttons silently; the ruling is
   recorded in CLAUDE.md's accessibility register. */
/* ⚠ ORDER MATTERS AND IT BIT ONCE: this block sat BELOW the announcement
   bar originally. `customElements.define` upgrades matching elements
   already in the DOM SYNCHRONOUSLY, so AnnouncementBar's connectedCallback
   ran before `const DR_MOTION_KEY` was initialised and threw a TDZ
   ReferenceError. The function declarations hoist; the const does not.
   Keep this above every consumer.*/
/**/
class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    whenParsed(() => this.init());
  }

  init() {
    if (this.initialized) {
      this.resume();
      return;
    }
    this.initialized = true;
    this.messages = Array.from(this.querySelectorAll('[data-usp]'));
    if (this.messages.length === 0) return;
    this.index = 0;
    this.rotateMs = parseInt(this.dataset.rotateInterval || '0', 10);
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.addEventListener('mouseenter', () => this.pause());
    this.addEventListener('mouseleave', () => this.resume());
    this.addEventListener('focusin', () => this.pause());
    this.addEventListener('focusout', () => this.resume());
    this.resume();
  }

  disconnectedCallback() {
    this.pause();
  }

  pause() {
    clearInterval(this.timer);
    this.timer = null;
  }

  resume() {
    if (this.rotateMs >= 1000 && this.messages.length > 1 && !this.reduced.matches && !this.timer) {
      this.timer = setInterval(() => this.advance(), this.rotateMs);
    }
  }

  advance() {
    this.messages[this.index].hidden = true;
    this.index = (this.index + 1) % this.messages.length;
    this.messages[this.index].hidden = false;
  }
}
customElements.define('announcement-bar', AnnouncementBar);

/**/
/* Hero slideshow: fade slides, arrows + dash indicators, optional*/
/* Autoplay (pauses on hover/focus, off under reduced motion).*/
/**/
class HeroSlideshow extends HTMLElement {
  connectedCallback() {
    whenParsed(() => this.init());
  }

  init() {
    if (this.initialized) {
      this.resume();
      return;
    }
    this.initialized = true;
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    if (this.slides.length === 0) return;
    this.dashes = Array.from(this.querySelectorAll('[data-slide-to]'));
    this.index = 0;
    this.autoplayMs = parseInt(this.dataset.autoplay || '0', 10);
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.querySelector('[data-slide-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
    this.querySelector('[data-slide-next]')?.addEventListener('click', () => this.go(this.index + 1, true));
    this.dashes.forEach((d) => d.addEventListener('click', () => this.go(Number(d.dataset.slideTo), true)));
    this.addEventListener('mouseenter', () => this.pause());
    this.addEventListener('mouseleave', () => this.resume());
    this.addEventListener('focusin', () => this.pause());
    this.addEventListener('focusout', (e) => {
      if (!this.contains(e.relatedTarget)) this.resume();
    });
    this.bindSwipe();
    this.go(0);
    this.resume();
  }

  /*
   * 🔴 — "the swipe's not working now". It was never working:
   * THERE WAS NO SWIPE HANDLER AT ALL. The hero cross-fades between
   * absolutely-stacked slides toggled by class, so there is no native
   * scroller to swipe and nothing ever listened for a drag. With the arrows
   * now removed on mobile, this stopped being a
   * missing nicety and became the only way to reach slides 2-6 on a phone.

   * ⚠ Listen on the HOST, not on the slides: `go` marks every inactive
   * slide `inert`, and an inert subtree does not dispatch pointer events —
   * binding per slide would work for exactly one slide and then die.
   * ⚠ Gated to touch/pen. A mouse drag across the hero must not page it.
   * ⚠ Horizontal delta must beat the vertical one, or flicking down the page
   * advances the hero on the way past.
   * ⚠ A manual advance pauses autoplay, matching the arrows and dashes —
   * a slide moving under the reader's thumb is the thing autoplay-pause is
   * for.
   */
  bindSwipe() {
    if (this.slides.length < 2) return;
    let x0 = null;
    let y0 = null;
    this.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse') return;
      x0 = e.clientX; y0 = e.clientY;
    }, { passive: true });
    this.addEventListener('pointerup', (e) => {
      if (x0 === null) return;
      const dx = e.clientX - x0;
      const dy = e.clientY - y0;
      x0 = null; y0 = null;
      if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy)) return;
      this.go(this.index + (dx < 0 ? 1 : -1), true);
    }, { passive: true });
    this.addEventListener('pointercancel', () => { x0 = null; y0 = null; }, { passive: true });
  }

  disconnectedCallback() {
    this.pause();
  }

  pause() {
    clearInterval(this.timer);
    this.timer = null;
  }

  resume() {
    if (this.autoplayMs >= 2000 && this.slides.length > 1 && !this.reduced.matches && !this.timer) {
      this.timer = setInterval(() => this.go(this.index + 1), this.autoplayMs);
    }
  }

  go(to, manual = false) {
    const n = this.slides.length;
    const previousIndex = this.index;
    this.index = ((to % n) + n) % n;
    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.classList.toggle('is-active', active);
      slide.toggleAttribute('inert', !active);
    });
    this.dashes.forEach((d, i) => d.setAttribute('aria-current', String(i === this.index)));
    if (previousIndex !== this.index) {
      this.dispatchEvent(new CustomEvent('dr:hero-slide-change', { bubbles: true }));
    }
    // optional "01 / 04" readout
    const counter = this.querySelector('[data-slide-counter]');
    if (counter) counter.textContent = `${String(this.index + 1).padStart(2, '0')} / ${String(n).padStart(2, '0')}`;
    if (manual) this.pause();
    // the video list was indexed against the SLIDE list, so
    // a single video on slide 4 was checked against slide 1: it played
    // under the wrong slide and PAUSED on its own (the client's "video
    // doesn't work"). Each video answers to the slide that contains it.
    this.querySelectorAll('video[data-slide-video]').forEach((v) => {
      const active = v.closest('[data-slide]')?.classList.contains('is-active');
      if (active && !this.reduced.matches) v.play?.().catch(() => {});
      else v.pause?.();
    });
  }
}
customElements.define('hero-slideshow', HeroSlideshow);

/**/
/* Horizontal scroll carousel with the shared draggable scrollbar*/
/* (base.css .carousel-progress): press anywhere on the track to jump,*/
/* Drag to scrub. Wheel scrolls natively; the scroller stays focusable*/
/* So arrow keys work.*/
/**/
class ScrollCarousel extends HTMLElement {
  connectedCallback() {
    whenParsed(() => this.init());
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;
    this.scroller = this.querySelector('[data-scroller]');
    this.fill = this.querySelector('[data-progress-fill]');
    this.handle = this.querySelector('[data-progress-handle]');
    this.track = this.querySelector('[data-progress-track]');
    if (!this.scroller) return;
    // a mouse drag across a card started the browser's NATIVE image
    // drag as well, so the pointer carried a ghost of the artwork while the
    // track panned. Cancelling dragstart on the scroller kills the ghost
    // without touching the pan (pointer events) or the click-through to the
    // card link. Touch never raises dragstart, so it is unaffected.
    this.scroller.addEventListener('dragstart', (event) => event.preventDefault());
    this.scroller.addEventListener('scroll', () => this.sync(), { passive: true });
    this.onResize = () => this.sync();
    window.addEventListener('resize', this.onResize);
    if (this.track) {
      this.track.addEventListener('pointerdown', (event) => this.scrubStart(event));
    }
    // 215 J4: the featured carousel's hover arrows (data-scroll-move) are retired — drag, the progress bar and the keyboard (focusable scroller) carry it.
    this.initDrag();
    this.initTouchAxis();
    this.sync();
  }

  /* 🔴 214 J6 — TOUCH AXIS LOCK, decided on the first ~10px and then owned.
     Vertical intent: the rail must not nudge or re-snap while the page
     scrolls through it, so its horizontal scrolling and snapping are
     suspended for the gesture (restored on release). Horizontal intent: the
     rail owns it and the smooth-scroll layer is told to stand aside
     (`data-lenis-prevent`, the same handover every scrollable overlay
     declares). Native scrolling is never re-implemented — this only removes
     the two ways the axes could bleed into each other. */
  initTouchAxis() {
    if (!this.scroller || this.axisWired) return;
    this.axisWired = true;
    let x0 = null; let y0 = null; let axis = null;
    const restore = () => {
      if (axis === 'y') { this.scroller.style.overflowX = ''; this.scroller.style.scrollSnapType = ''; }
      if (axis === 'x') this.scroller.removeAttribute('data-lenis-prevent');
      x0 = null; y0 = null; axis = null;
    };
    this.scroller.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1) return;
      x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; axis = null;
    }, { passive: true });
    this.scroller.addEventListener('touchmove', (e) => {
      if (x0 === null || axis !== null || e.touches.length !== 1) return;
      const dx = Math.abs(e.touches[0].clientX - x0);
      const dy = Math.abs(e.touches[0].clientY - y0);
      if (dx < 10 && dy < 10) return;
      axis = dx > dy ? 'x' : 'y';
      if (axis === 'y') { this.scroller.style.overflowX = 'hidden'; this.scroller.style.scrollSnapType = 'none'; }
      else this.scroller.setAttribute('data-lenis-prevent', '');
    }, { passive: true });
    this.scroller.addEventListener('touchend', restore, { passive: true });
    this.scroller.addEventListener('touchcancel', restore, { passive: true });
  }

  /* 21.2: mouse pointer-drag with momentum on the native scroller. Touch
     keeps native scrolling (never intercepted); snap is suspended while
     dragging or the proximity snap yanks the glide back.
     The click that follows a real drag is suppressed so a drag never
     triggers a card link.*/
  initDrag() {
    if (!this.scroller || this.dragWired) return;
    this.dragWired = true;
    let startX = 0;
    let startLeft = 0;
    let lastX = 0;
    let lastT = 0;
    let velocity = 0;
    let dragging = false;
    let moved = 0;
    const stopMomentum = () => cancelAnimationFrame(this.momentumRaf);
    this.scroller.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      stopMomentum();
      dragging = true;
      moved = 0;
      startX = lastX = event.clientX;
      startLeft = this.scroller.scrollLeft;
      lastT = performance.now();
      velocity = 0;
      this.scroller.classList.add('is-dragging');
    });
    window.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      const dx = event.clientX - startX;
      moved = Math.max(moved, Math.abs(dx));
      this.scroller.scrollLeft = startLeft - dx;
      const now = performance.now();
      const dt = now - lastT;
      if (dt > 0) velocity = (event.clientX - lastX) / dt;
      lastX = event.clientX;
      lastT = now;
    });
    const release = () => {
      if (!dragging) return;
      dragging = false;
      // momentum: carry release velocity, decay ~5%/frame, stop under 0.02px/ms
      let v = velocity * 16; // px per frame
      const glide = () => {
        if (Math.abs(v) < 0.4) {
          this.scroller.classList.remove('is-dragging');
          return;
        }
        this.scroller.scrollLeft -= v;
        v *= 0.94;
        this.momentumRaf = requestAnimationFrame(glide);
      };
      if (Math.abs(v) > 2) this.momentumRaf = requestAnimationFrame(glide);
      else this.scroller.classList.remove('is-dragging');
    };
    window.addEventListener('pointerup', release);
    window.addEventListener('pointercancel', release);
    // a drag must not click through to the card link
    this.scroller.addEventListener('click', (event) => {
      if (moved > 6) {
        event.preventDefault();
        event.stopPropagation();
        moved = 0;
      }
    }, true);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
  }

  scrubStart(event) {
    if (event.button > 0) return;
    event.preventDefault();
    try {
      this.track.setPointerCapture(event.pointerId);
    } catch (e) { /* Pointer already released*/ }
    this.track.classList.add('is-scrubbing');
    // scroll-snap fights the scrub (it yanks the jump back to the nearest
    // tile edge) — suspend it for the duration of the pointer interaction
    this.snapType = this.scroller.style.scrollSnapType;
    this.scroller.style.scrollSnapType = 'none';
    // item 5: grabbing the HANDLE anchors the drag at the grab point (no
    // teleport); pressing the empty track jumps there, then drags centred
    const handleRect = this.handle?.getBoundingClientRect();
    const onHandle = handleRect && event.clientX >= handleRect.left && event.clientX <= handleRect.right;
    this.grabOffset = onHandle
      ? event.clientX - handleRect.left
      : (this.handle ? this.handle.offsetWidth / 2 : 0);
    if (!onHandle) this.scrubTo(event.clientX);
    const move = (e) => this.scrubTo(e.clientX);
    const end = () => {
      this.track.classList.remove('is-scrubbing');
      this.scroller.style.scrollSnapType = this.snapType || '';
      this.track.removeEventListener('pointermove', move);
      this.track.removeEventListener('pointerup', end);
      this.track.removeEventListener('pointercancel', end);
    };
    this.track.addEventListener('pointermove', move);
    this.track.addEventListener('pointerup', end);
    this.track.addEventListener('pointercancel', end);
  }

  scrubTo(clientX) {
    const rect = this.track.getBoundingClientRect();
    const handleWidth = this.handle ? this.handle.offsetWidth : 0;
    const usable = rect.width - handleWidth;
    const grab = this.grabOffset ?? handleWidth / 2;
    // handle-left at X% of the usable track → content at X% of its range
    const ratio = usable > 0 ? Math.min(1, Math.max(0, (clientX - rect.left - grab) / usable)) : 0;
    this.scroller.scrollLeft = ratio * (this.scroller.scrollWidth - this.scroller.clientWidth);
  }

  sync() {
    const max = this.scroller.scrollWidth - this.scroller.clientWidth;
    const ratio = max > 0 ? this.scroller.scrollLeft / max : 0;
    const visible = this.scroller.scrollWidth > 0 ? this.scroller.clientWidth / this.scroller.scrollWidth : 1;
    if (this.handle && this.track) {
      // PROPORTIONAL handle — its length mirrors the visible
      // fraction (like a real scrollbar), so drag distance maps ~1:1 to
      // content distance on short rails too.adds the CEILING:
      // fully-honest proportion on a low-overflow rail (Designer
      // Collections ≈ × viewport) made the handle near-track-length
      // and a few px of drag flew to the end — clamp to half the track.
      // scrubToreads the live offsetWidth, so the drag mapping stays
      // 1:1 against the CLAMPED handle: no dead zone, no overshoot.
      const trackWidth = this.track.getBoundingClientRect().width;
      const floor = parseFloat(getComputedStyle(this.handle).minWidth) || 48;
      const handleWidth = Math.min(Math.max(floor, visible * trackWidth), trackWidth * 0.5);
      this.handle.style.width = `${handleWidth}px`;
      this.handle.style.left = `calc(${ratio * 100}% - ${ratio * handleWidth}px)`;
    }
    if (this.fill) this.fill.style.width = `${Math.max(visible, ratio) * 100}%`;
    if (this.track) this.track.hidden = max <= 0;
  }
}
customElements.define('scroll-carousel', ScrollCarousel);

/**/
/* YouTube lightbox — the site-wide video pattern: a round play icon over a
   poster image, opening the shared lightbox.*/
/* Play icon opens a focus-trapped dialog with a chrome-hidden embed.*/
/* The iframe only loads when opened.*/
/**/
class VideoLightbox extends HTMLElement {
  connectedCallback() {
    whenParsed(() => this.init());
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;
    this.dialog = this.querySelector('[data-lightbox]');
    this.frameHolder = this.querySelector('[data-lightbox-frame]');
    if (!this.dialog || !this.frameHolder) return;
    this.querySelector('[data-lightbox-open]')?.addEventListener('click', (event) => {
      this.open(event.currentTarget);
    });
    this.dialog.addEventListener('click', (event) => {
      if (event.target.closest('[data-lightbox-close]') || event.target === this.dialog) this.close();
    });
    this.dialog.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') this.close();
    });
  }

  open(trigger) {
    this.trigger = trigger;
    const src = this.dataset.videoSrc;
    const id = this.dataset.videoId;
    if (!src && !id) return;
    if (src) {
      /* Self-hosted MP4 (re-hosted to Shopify Files) — native video
         in the same chrome. Built via DOM, not an HTML string.*/
      const video = document.createElement('video');
      video.src = src;
      video.controls = true;
      video.autoplay = true;
      video.playsInline = true;
      video.setAttribute('title', this.dataset.videoTitle || 'Video');
      this.frameHolder.replaceChildren(video);
    } else {
      this.frameHolder.innerHTML = `<iframe src="https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1&modestbranding=1&rel=0&playsinline=1" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen title="${this.dataset.videoTitle || 'Video'}"></iframe>`;
    }
    this.dialog.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    window.trapFocus(this.dialog, this.dialog.querySelector('[data-lightbox-close]'));
  }

  close() {
    this.dialog.hidden = true;
    this.frameHolder.innerHTML = '';
    document.documentElement.style.overflow = '';
    window.removeTrapFocus(this.trigger || null);
  }
}
customElements.define('video-lightbox', VideoLightbox);

/**/
/* Gallery lightbox. NOT a second lightbox implementation*/
/* It reuses the video lightbox's dialog chrome + motion layer and*/
/* The shared trapFocus helpers; what it adds is stepping*/
/* Through a set of images. Any [data-gallery-item] inside the element*/
/* Becomes a slide, in DOM order, so the journal 12-up, the project*/
/* 12-up and the project feature rows all wire up the same way.*/
/**/
class GalleryLightbox extends HTMLElement {
  connectedCallback() {
    whenParsed(() => this.init());
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;
    this.dialog = this.querySelector('[data-gallery-dialog]');
    this.img = this.querySelector('[data-gallery-img]');
    this.caption = this.querySelector('[data-gallery-caption]');
    this.counter = this.querySelector('[data-gallery-counter]');
    this.items = Array.from(this.querySelectorAll('[data-gallery-item]'));
    if (!this.dialog || !this.img || !this.items.length) return;

    // Each item exposes the full-size source + its caption; the grid keeps
    // its own responsive thumbnail markup untouched.
    this.slides = this.items.map((el) => ({
      src: el.dataset.galleryFull || el.querySelector('img')?.currentSrc || el.querySelector('img')?.src || '',
      alt: el.dataset.galleryAlt || el.querySelector('img')?.alt || '',
      caption: el.dataset.galleryCaption || '',
      el,
    }));

    this.items.forEach((el, i) => {
      el.setAttribute('role', 'button');
      el.setAttribute('tabindex', '0');
      if (!el.getAttribute('aria-label')) {
        el.setAttribute('aria-label', `Open image ${i + 1} of ${this.items.length}`);
      }
      el.addEventListener('click', () => this.open(i));
      el.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.open(i);
        }
      });
    });

    this.dialog.addEventListener('click', (event) => {
      if (event.target.closest('[data-gallery-close]') || event.target === this.dialog) this.close();
      if (event.target.closest('[data-gallery-prev]')) this.step(-1);
      if (event.target.closest('[data-gallery-next]')) this.step(1);
    });
    // keydown, not keyup: arrows must not scroll the trapped dialog
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
      else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.step(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.step(1);
      }
    });
  }

  paint() {
    const s = this.slides[this.index];
    if (!s) return;
    this.img.src = s.src;
    this.img.alt = s.alt;
    if (this.caption) {
      this.caption.textContent = s.caption;
      this.caption.hidden = !s.caption;
    }
    if (this.counter) this.counter.textContent = `${this.index + 1} / ${this.slides.length}`;
    // warm only the neighbours — a 12-up should not fetch twelve full-size files
    [this.index - 1, this.index + 1].forEach((n) => {
      const near = this.slides[(n + this.slides.length) % this.slides.length];
      if (near && near.src) new Image().src = near.src;
    });
    const single = this.slides.length < 2;
    this.querySelectorAll('[data-gallery-prev], [data-gallery-next]').forEach((b) => {
      b.hidden = single;
    });
  }

  step(delta) {
    if (this.slides.length < 2) return;
    this.index = (this.index + delta + this.slides.length) % this.slides.length;
    this.paint();
  }

  open(index) {
    this.index = index;
    this.trigger = this.items[index] || null;
    this.paint();
    this.dialog.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    window.trapFocus(this.dialog, this.dialog.querySelector('[data-gallery-close]'));
  }

  close() {
    this.dialog.hidden = true;
    document.documentElement.style.overflow = '';
    window.removeTrapFocus(this.trigger || null);
  }
}
customElements.define('gallery-lightbox', GalleryLightbox);

/**/
/* Browse By drawer:*/
/* Left slide-out with Sort + filter accordions. Filter state lives in*/
/* Native URL params (Search & Discovery) — the single source of*/
/* Truth. Ticking a filter KEEPS THE DRAWER OPEN and re-renders the*/
/* Grid + pagination + chips (both spots) live via the Section*/
/* Rendering API; the URL updates via pushState (shareable, back*/
/* Works). Chip × / CLEAR ALL run the same path. Drawer DOM is never*/
/* Replaced — scroll position, open accordions and focus all persist;*/
/* Only input checked-state syncs from the fetched doc. No-JS: the GET*/
/* Form + APPLY button + chip links navigate normally.*/
/* Auto-open once per visitor (localStorage), skipped under*/
/* Prefers-reduced-motion; any dismissal or manual open persists.*/
/**/
class BrowseByDrawer extends HTMLElement {
  connectedCallback() {
    whenParsed(() => this.init());
  }

  init() {
    this.drawer = this.querySelector('.browse-by');
    this.overlay = this.querySelector('[data-drawer-overlay]');
    this.form = this.querySelector('[data-filter-form]');
    if (!this.drawer) return;

    this.initSaleFilter();

    this.onTriggerClick = this.onTriggerClick || ((event) => {
      const trigger = event.target.closest('[data-browse-by-open]');
      if (!trigger) return;
      event.preventDefault();
      this.markSeen();
      this.open(trigger);
    });
    document.addEventListener('click', this.onTriggerClick);

    // chips live in BOTH spots (drawer + sticky bar) — intercept at the
    // document so either runs the live path; no-JS they stay links
    this.onChipClick = this.onChipClick || ((event) => {
      const saleChip = event.target.closest('[data-sale-chip-remove]');
      if (saleChip && this.hasAttribute('data-sale-filter')) {
        event.preventDefault();
        this.saleSelected.delete(Number(saleChip.dataset.saleChipRemove));
        this.applySaleFilter({ updateUrl: true });
        return;
      }
      const chip = event.target.closest('[data-chip-remove]');
      if (!chip || !this.dataset.sectionId) return;
      event.preventDefault();
      this.applyFilters(chip.href);
    });
    document.addEventListener('click', this.onChipClick);

    this.onPopState = this.onPopState || ((event) => {
      if (event.state?.drFilter) {
        if (this.hasAttribute('data-sale-filter')) {
          const stored = new URL(window.location.href).searchParams.get('sale_discount');
          this.saleSelected = new Set(stored === null
            ? this.saleTiers
            : stored.split(',').map(Number).filter((value) => this.saleTiers.includes(value)));
        }
        this.applyFilters(window.location.href, { push: false });
      }
    });
    window.addEventListener('popstate', this.onPopState);
    this.onListingAppended = this.onListingAppended || (() => this.applySaleFilter());
    document.addEventListener('dr:listing-appended', this.onListingAppended);

    if (!this.localBound) {
      this.localBound = true;
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]') || event.target === this.overlay) this.close();
      });
      this.addEventListener('keyup', (event) => {
        if (event.code === 'Escape') this.close();
      });
      if (this.form) {
        // live re-filter keeps state in the URL; the APPLY button is
        // the no-JS fallback and hides once JS is running
        this.form.querySelectorAll('[data-filter-apply]').forEach((btn) => (btn.hidden = true));
        this.form.addEventListener('change', (event) => {
          const saleTier = event.target.closest('[data-sale-tier]');
          if (saleTier) {
            const value = Number(saleTier.value);
            if (saleTier.checked) this.saleSelected.add(value);
            else this.saleSelected.delete(value);
            this.applySaleFilter({ updateUrl: true });
            return;
          }
          // the pre-ticked collection-context box is NOT a
          // filter param (nameless — it never serializes). Unticking it
          // drops the context: a REAL navigation to Shop All carrying
          // the other active filters — never the in-place morph, which
          // would swap the grid under the old collection's heading.
          const ctx = event.target.closest('[data-context-filter]');
          if (ctx && !ctx.checked) {
            window.location.href = ctx.dataset.contextRemoveUrl || (window.Shopify?.routes?.root || '/') + 'collections/all';
            return;
          }
          // ONE canonical URL per facet: exactly one active
          // filter value, no collection context, and a facet collection
          // mapped for it → NAVIGATE to that page (the SEO landing).
          // A contexted page counts its context as a filter, so this
          // can never fire there; unmapped axes (size bands, curated
          // status) fall through to the in-place morph.
          if (this.singleFacetUrl()) {
            clearTimeout(this.applyTimer);
            /* 218 J3 — carry WHERE the visitor came from, so the destination's
               pill and Clear all can return them there (All Carpets keeps its
               own return route; anything else returns to All Rugs). Read
               client-side on arrival, never inferred from the destination. */
            const dest = new URL(this.singleFacetUrl(), window.location.origin);
            if (/\/collections\/all-carpets(\/|$)/.test(window.location.pathname)) dest.searchParams.set('from', 'all-carpets');
            window.location.href = dest.toString();
            return;
          }
          // ~250ms debounce coalesces rapid ticks into one fetch (item 3)
          clearTimeout(this.applyTimer);
          this.applyTimer = setTimeout(() => this.applyFilters(), 250);
        });
        // ensure an initial history entry so Back returns cleanly
        if (!window.history.state?.drFilter) {
          window.history.replaceState({ drFilter: true }, '', window.location.href);
        }
      }
    }
    this.applyReturnRoute();
    this.scheduleAutoOpen();
  }

  initSaleFilter() {
    if (!this.hasAttribute('data-sale-filter')) return;
    this.saleTiers = (this.dataset.saleTiers || '10,20,30')
      .split(',').map((value) => Number(value.trim())).filter((value) => Number.isFinite(value) && value > 0);
    const params = new URL(window.location.href).searchParams;
    const stored = params.get('sale_discount');
    this.saleSelected = new Set(
      stored === null
        ? this.saleTiers
        : stored.split(',').map(Number).filter((value) => this.saleTiers.includes(value)),
    );
    this.applySaleFilter();
  }

  applySaleFilter({ updateUrl = false } = {}) {
    if (!this.hasAttribute('data-sale-filter')) return;
    const cells = [...document.querySelectorAll('[data-listing-grid] .listing-grid__cell')];
    let visible = 0;
    cells.forEach((cell) => {
      const card = cell.querySelector('[data-sale-discount]');
      const show = !!card && this.saleSelected.has(Number(card.dataset.saleDiscount));
      cell.hidden = !show;
      if (show) visible += 1;
    });
    document.querySelectorAll('[data-sale-tier]').forEach((input) => {
      input.checked = this.saleSelected.has(Number(input.value));
    });
    document.querySelectorAll('[data-sale-filter-chips]').forEach((holder) => {
      holder.replaceChildren(...[...this.saleSelected].sort((a, b) => a - b).map((tier) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'filter-chips__chip';
        button.dataset.saleChipRemove = String(tier);
        button.setAttribute('aria-label', `Remove Sale Discount: ${tier}% OFF RRP`);
        const label = document.createElement('span');
        label.textContent = `${tier}% OFF RRP`;
        const close = document.createElement('span');
        close.setAttribute('aria-hidden', 'true');
        close.textContent = '×';
        button.append(label, close);
        return button;
      }));
      holder.hidden = this.saleSelected.size === 0;
    });
    const grid = document.querySelector('[data-listing-grid]');
    let empty = grid?.querySelector('[data-sale-filter-empty]');
    if (grid && !empty) {
      empty = document.createElement('p');
      empty.className = 'listing-grid__empty';
      empty.dataset.saleFilterEmpty = '';
      empty.textContent = 'No sale products match these discount filters.';
      grid.append(empty);
    }
    if (empty) empty.hidden = visible > 0;
    if (updateUrl) {
      const url = new URL(window.location.href);
      const allSelected = this.saleSelected.size === this.saleTiers.length
        && this.saleTiers.every((tier) => this.saleSelected.has(tier));
      if (allSelected) url.searchParams.delete('sale_discount');
      else url.searchParams.set('sale_discount', [...this.saleSelected].sort((a, b) => a - b).join(','));
      window.history.pushState({ drFilter: true }, '', url.pathname + url.search);
    }
  }
  /* 218 J3 — the return route. A navigated facet page carries `?from=all-carpets`
     when the visitor came from All Carpets; the context pill's × and Clear all
     then return there instead of All Rugs (the default the server renders).
     Client-side by rule (§0(a)/(jj)): the query string is not a cache key. */
  applyReturnRoute() {
    let from = null;
    try { from = new URL(window.location.href).searchParams.get('from'); } catch (e) { return; }
    if (from !== 'all-carpets') return;
    const parent = `${window.Shopify?.routes?.root || '/'}collections/all-carpets`;
    const rewrite = (a) => {
      if (!a) return;
      try { const u = new URL(a.getAttribute('href'), window.location.origin); if (!/\/collections\/all(\/|$)/.test(u.pathname)) return; const p = new URL(parent, window.location.origin); p.search = u.search; a.setAttribute('href', p.pathname + p.search); a.dataset.returnRoute = 'all-carpets'; } catch (e) { /* leave the server's href */ }
    };
    document.querySelectorAll('[data-filter-chips] a, .browse-by__clear').forEach(rewrite); // only hrefs on /collections/all are rewritten
    document.querySelectorAll('[data-context-filter]').forEach((i) => { if (i.dataset.contextRemoveUrl) { const u = new URL(i.dataset.contextRemoveUrl, window.location.origin); const p = new URL(parent, window.location.origin); p.search = u.search; i.dataset.contextRemoveUrl = p.pathname + p.search; } });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onTriggerClick);
    document.removeEventListener('click', this.onChipClick);
    window.removeEventListener('popstate', this.onPopState);
    document.removeEventListener('dr:listing-appended', this.onListingAppended);
    clearTimeout(this.autoTimer);
    clearTimeout(this.applyTimer);
    this.aborter?.abort();
  }

  filterUrl() {
    const base = this.form.getAttribute('action').split('?')[0];
    const params = new URLSearchParams(new FormData(this.form));
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  }

  /* Returns the facet collection URL when EXACTLY one filter
     value is active, this page has no collection context, and the
     axis:value pair is in the rendered facet map; null otherwise.*/
  singleFacetUrl() {
    if (this.querySelector('[data-context-filter]')) return null;
    if (!this.facetMap) {
      try {
        this.facetMap = JSON.parse(this.querySelector('[data-facet-map]')?.textContent || '{}');
      } catch (e) {
        this.facetMap = {};
      }
    }
    const entries = [...new URLSearchParams(new FormData(this.form)).entries()]
      .filter(([k, v]) => k.startsWith('filter.') && v !== '');
    if (entries.length !== 1) return null;
    const [key, value] = entries[0];
    const tail = key.split('.').pop();
    /* `designer` and `collection` (the range axis) join the
       routed set. They map to themselves because the drawer's map now keys
       designer/range collections by their own title.
       ⚠ Everything absent from this table still filters IN PLACE, which is
       how deliberate exclusions — size bands and curated status —
       keep working. Adding an axis here without a matching map entry is a
       no-op, not a break: the lookup below simply returns null.*/
    const axis = {
      colour_family: 'colour', style: 'style', shape: 'shape', construction: 'construction',
      designer: 'designer', collection: 'collection',
    }[tail];
    if (!axis) return null;
    return this.facetMap[`${axis}:${value}`] || null;
  }

  async applyFilters(url = this.filterUrl(), { push = true } = {}) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) {
      window.location.href = url; // no section context — degrade to navigation
      return;
    }
    // chips fetched via the SRA carry section_id inside url_to_remove —
    // scrub it so the shareable URL and the next fetch stay clean
    const cleanUrl = new URL(url, window.location.origin);
    cleanUrl.searchParams.delete('section_id');
    this.aborter?.abort();
    this.aborter = new AbortController();
    const grid = document.querySelector('[data-listing-grid]');
    grid?.setAttribute('aria-busy', 'true');
    try {
      const fetchUrl = new URL(cleanUrl);
      fetchUrl.searchParams.set('section_id', sectionId);
      const res = await fetch(fetchUrl, { signal: this.aborter.signal });
      if (!res.ok) throw new Error(`section fetch ${res.status}`);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      this.renderResults(doc);
      if (push) window.history.pushState({ drFilter: true }, '', cleanUrl.pathname + cleanUrl.search);
      document.dispatchEvent(new CustomEvent('dr:filters-applied'));
      grid?.removeAttribute('aria-busy');
    } catch (e) {
      grid?.removeAttribute('aria-busy');
      if (e.name === 'AbortError') return; // superseded by a newer tick
      window.location.href = cleanUrl; // degrade to full navigation
    }
  }

  renderResults(doc) {
    /* 🔴 sweep — innerHTML DESTROYS THE FOCUSED ELEMENT.
       The accordion region below already restores focus and so does
       the cart drawer, but the GRID and the CHIPS did not. Removing a filter
       chip with the keyboard is the reachable case: the chip you activated
       is replaced mid-press and focus falls to <body>, dropping the visitor
       at the top of the document. 's floor, not polish.*/
    const active = document.activeElement;

    // grid content (grid scroll position: the node itself stays put)
    const grid = document.querySelector('[data-listing-grid]');
    const freshGrid = doc.querySelector('[data-listing-grid]');
    const gridHadFocus = !!active && !!grid && grid.contains(active);
    if (grid && freshGrid) grid.innerHTML = freshGrid.innerHTML;
    if (gridHadFocus) grid.querySelector('a, button')?.focus({ preventScroll: true });

    // chips — both spots re-render from the same fetched state
    document.querySelectorAll('[data-filter-chips]').forEach((el) => {
      const fresh = doc.querySelector(`[data-filter-chips="${el.dataset.filterChips}"]`);
      if (!fresh) return;
      /* Keep the visitor's PLACE IN THE ROW, not a specific node: the chip
         they removed no longer exists, so land on whatever now occupies that
         position, else the last chip, else the control that opens the drawer.
         Never leave focus on <body>.*/
      const focusables = (n) => [...n.querySelectorAll('a, button')];
      const idx = active && el.contains(active) ? focusables(el).indexOf(active) : -1;
      el.innerHTML = fresh.innerHTML;
      el.hidden = fresh.hasAttribute('hidden');
      if (idx >= 0) {
        const now = el.hidden ? [] : focusables(el);
        const target = now[Math.min(idx, now.length - 1)]
          || document.querySelector('[data-browse-by-open]');
        target?.focus({ preventScroll: true });
      }
    });

    // results count line (search page)
    const note = document.querySelector('.listing__note[role="status"]');
    const freshNote = doc.querySelector('.listing__note[role="status"]');
    if (note && freshNote) note.innerHTML = freshNote.innerHTML;

    // progressive narrowing (locked ): the fresh render
    // OMITS zero-count values entirely, so the accordion region must
    // re-render or stale options linger for the whole session (the old
    // input-only sync deliberately never refreshed it). Open accordions,
    // drawer scroll and focus are restored by hand so the keep-open
    // behaviour is not regressed.
    const freshForm = doc.querySelector('[data-filter-form]');
    const region = this.form?.querySelector('[data-filter-accordions]');
    const freshRegion = freshForm?.querySelector('[data-filter-accordions]');
    let built = null;
    if (region && freshRegion) {
      const labelOf = (d) => d.querySelector('.browse-by__group-label span:first-child')?.textContent.trim();
      const openLabels = new Set([...region.querySelectorAll('details[open]')].map(labelOf).filter(Boolean));
      const active = document.activeElement;
      const focusKey = active && region.contains(active) && active.name ? `${active.name}␟${active.value}` : null;
      const scrollEl = this.drawer?.querySelector('.browse-by__scroll') || this.drawer;
      const st = scrollEl ? scrollEl.scrollTop : 0;
      /* 218 J2 — the swap is ATOMIC. `innerHTML = …` first empties the region, the
         aside's scroll height collapses and the browser clamps scrollTop to 0
         before the fresh nodes land; the restore below then had to undo a jump
         the visitor could see. Building the fresh region off-DOM and replacing
         the node in one step means the height never collapses. Open groups are
         restored by label first, focus by name/value WITHOUT scrolling, then
         the captured scrollTop is written back as the belt. */
      built = freshRegion.cloneNode(true);
      built.querySelectorAll('details').forEach((d) => { if (openLabels.has(labelOf(d))) d.open = true; });
      region.replaceWith(built);
      this._swapFocus = focusKey; // p141: prefixed instance state, never a bare IDL-colliding name
      if (focusKey) {
        const [n, v] = focusKey.split('␟');
        built.querySelector(`input[name="${CSS.escape(n)}"][value="${CSS.escape(v)}"]`)?.focus({ preventScroll: true });
      }
      if (scrollEl) scrollEl.scrollTop = st;
    }
    // inputs OUTSIDE the accordion region (availability, sort) keep the
    // in-place checked-state sync
    if (this.form && freshForm) {
      const key = (i) => `${i.name}␟${i.value}`;
      const freshInputs = new Map();
      freshForm.querySelectorAll('input[name]').forEach((i) => freshInputs.set(key(i), i));
      this.form.querySelectorAll('input[type="checkbox"][name], input[type="radio"][name]').forEach((i) => {
        if (region && (built || region).contains(i)) return;
        const f = freshInputs.get(key(i));
        if (f) i.checked = f.checked;
      });
    }

    // hand the fresh pagination to the infinite scroller
    document.querySelector('infinite-listing')?.reset(doc);
    this.applySaleFilter();
  }

  seen() {
    try {
      return localStorage.getItem('dr:browseby-seen') === '1';
    } catch (e) {
      return true;
    }
  }

  markSeen() {
    try {
      localStorage.setItem('dr:browseby-seen', '1');
    } catch (e) {
      /* Private mode — fine*/
    }
  }

  scheduleAutoOpen() {
    if (!this.hasAttribute('data-auto-open')) return;
    if (this.seen()) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    clearTimeout(this.autoTimer);
    this.autoTimer = setTimeout(() => {
      if (this.seen() || !this.drawer.hidden) return;
      this.markSeen();
      this.open(null);
    }, Number(this.dataset.autoOpenDelay || 3000));
  }

  open(triggeredBy) {
    this.activeTrigger = triggeredBy || this.activeTrigger;
    clearTimeout(this.autoTimer);
    if (this.overlay) this.overlay.hidden = false;
    this.drawer.hidden = false;
    // the sticky bar (with its chips) counts as filter UI, not "outside" —
    // CSS lifts it above the click-away overlay while open (item 2).
    // NB attribute name must NOT collide with the [data-browse-by-open]
    // trigger hook — closestwalks to <html> (caught live: every click
    // in the open drawer matched the trigger and got preventDefault'd).
    document.documentElement.dataset.drBrowsebyOpen = '';
    document.documentElement.style.overflow = 'hidden';
    window.trapFocus(this.drawer, this.drawer.querySelector('[data-drawer-close]') || this.drawer);
  }

  close() {
    this.markSeen();
    if (this.overlay) this.overlay.hidden = true;
    this.drawer.hidden = true;
    delete document.documentElement.dataset.drBrowsebyOpen;
    document.documentElement.style.overflow = '';
    window.removeTrapFocus(this.activeTrigger || null);
  }
}
customElements.define('browse-by-drawer', BrowseByDrawer);

/* 222 J1 — the mobile (<=749) filter drawer for the projects + journal listings.
   Reuses the Browse By `.browse-by` chrome (`snippets/filter-drawer.liquid`) but
   has no Section-Rendering plumbing: the filter values inside are the listing's
   own <a> links (client-side for projects, real /tagged/ URLs for journal), so
   this element only opens/closes, traps focus and locks scroll. Progressive
   enhancement: it reveals the trigger and hides the inline rows (via the
   `filter-drawer-ready` html class) only once it has upgraded. */
class FilterDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('.filter-drawer__panel');
    this.overlay = this.querySelector('[data-drawer-overlay]');
    this.trigger = this.querySelector('[data-filter-drawer-open]');
    if (!this.panel) return;
    document.documentElement.classList.add('filter-drawer-ready');
    if (this.trigger) this.trigger.hidden = false;
    this.trapFocus = this.trapFocus.bind(this);
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-filter-drawer-open]')) { event.preventDefault(); this.open(); }
      else if (event.target.closest('[data-drawer-close]') || event.target === this.overlay) { this.close(); }
    });
    this.addEventListener('keyup', (event) => { if (event.code === 'Escape') this.close(); });
    this.updateChips();
    this.filterObserver = new MutationObserver(() => this.updateChips());
    this.filterObserver.observe(this.panel.querySelector('.browse-by__form'), {
      subtree: true, attributes: true, attributeFilter: ['class', 'href', 'aria-current']
    });
  }
  updateChips() {
    const filters = [];
    this.panel.querySelectorAll('.browse-by__option.is-active').forEach((option) => {
      const label = option.textContent.trim();
      if (!label || label.toLowerCase() === 'all') return;
      const axis = option.closest('[data-filter-drawer-axis]')?.dataset.filterDrawerAxis;
      const target = new URL(location.href);
      if (axis) {
        if (!target.searchParams.has(axis)) return;
        target.searchParams.delete(axis);
        target.searchParams.delete('page');
      } else {
        target.href = this.panel.querySelector('.browse-by__clear').href;
      }
      filters.push({ label, href: target.pathname + target.search });
    });
    const signature = JSON.stringify(filters);
    if (signature === this.chipSignature) return;
    this.chipSignature = signature;
    this.querySelectorAll('[data-blog-filter-chips]').forEach((container) => {
      container.replaceChildren(...filters.map(({ label, href }) => {
        const link = document.createElement('a');
        link.className = 'filter-drawer__chip';
        link.href = href;
        link.textContent = label + ' ×';
        link.setAttribute('aria-label', 'Remove filter: ' + label);
        return link;
      }));
    });
  }
  open() {
    if (!this.panel.hidden) return;
    this.panel.hidden = false;
    if (this.overlay) this.overlay.hidden = false;
    document.documentElement.classList.add('filter-drawer-open');
    this.resumeLenis = window.DRMotion?.lenis && !window.DRMotion.lenis.isStopped;
    window.DRMotion?.lenis?.stop();
    // Scroll locking can move sticky chrome out of view without changing its
    // state flag. Anchor this panel to the visible chrome, not its full height.
    this.syncPanelTop = () => {
      const bottom = Math.max(0, ...['.announcement-bar', '.site-header'].map((selector) => {
        const rect = document.querySelector(selector)?.getBoundingClientRect();
        return rect && rect.top < window.innerHeight ? rect.bottom : 0;
      }));
      this.style.setProperty('--filter-drawer-top', `${Math.min(bottom, window.innerHeight)}px`);
    };
    this.syncPanelTop();
    window.addEventListener('resize', this.syncPanelTop);
    this.trigger?.setAttribute('aria-expanded', 'true');
    this.panel.addEventListener('keydown', this.trapFocus);
    // land focus on the close button, never scrolling the locked page
    this.panel.querySelector('[data-drawer-close]')?.focus({ preventScroll: true });
  }
  close() {
    if (this.panel.hidden) return;
    this.panel.hidden = true;
    if (this.overlay) this.overlay.hidden = true;
    document.documentElement.classList.remove('filter-drawer-open');
    window.removeEventListener('resize', this.syncPanelTop);
    this.style.removeProperty('--filter-drawer-top');
    if (this.resumeLenis && document.documentElement.style.overflow !== 'hidden') window.DRMotion?.lenis?.start();
    this.trigger?.setAttribute('aria-expanded', 'false');
    this.panel.removeEventListener('keydown', this.trapFocus);
    this.trigger?.focus({ preventScroll: true });
  }
  trapFocus(event) {
    if (event.key !== 'Tab') return;
    const els = [...this.panel.querySelectorAll('a[href], button:not([disabled]), summary, input:not([tabindex="-1"])')]
      .filter((el) => !el.hidden && el.offsetParent !== null);
    if (!els.length) return;
    const first = els[0], last = els[els.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }
}
customElements.define('filter-drawer', FilterDrawer);

/**/
/* Listing view-mode control: GDS view-single /*/
/* View-grid icons switch the grid density; choice persists per*/
/* Visitor. The grid re-renders purely via a data attribute + CSS.*/
/**/
class ListingControls extends HTMLElement {
  connectedCallback() {
    whenParsed(() => this.init());
  }

  /* 🔴 THE TOGGLE IS MOMENTARY VIEW
     STATE FOR THE CURRENT PAGE ONLY. EVERY NAVIGATION SERVES THE DEFAULT.

     It used to persist the chosen view to localStorage, and because initapplied that value unconditionally to whatever [data-listing-grid] the
     page rendered, ONE press of 3-up anywhere silently overrode the
     server-rendered default on all four surfaces that carry the control —
     every PLP, /search, the five Browse-by hubs and the auction listing —
     for the rest of that visitor's life, with no way to reset it. It also
     re-applied after DOMContentLoaded, so a visitor whose stored view
     differed watched the grid reflow on every page load.

     The storage is gone in both directions: nothing reads it and nothing
     writes it, and the key is referenced nowhere else in the theme. Each
     section's server-rendered `data-view` is now the final word until the
     visitor presses a button on that page.
     ⚠ the toggle's DESIGN is frozen. This changes BEHAVIOUR only —
     the glyphs, the aria-pressed contract and the active-colour rule are
     untouched.*/
  init() {
    /* ⚠ ALL the grids on the page, not the first one. Every listing and every
       browse-by hub has exactly one, so this is a no-op there — but the
       Collections Hub renders its children in TWO groups (Studio and
       Collaborations), and a toggle that switched only the first would flip
       half the page and read as broken. */
    this.grids = [...document.querySelectorAll('[data-listing-grid]')];
    this.grid = this.grids[0];
    if (!this.grid) return;
    if (this.localBound) return;
    this.localBound = true;
    this.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-view-mode]');
      if (btn) this.setMode(btn.dataset.viewMode);
    });
  }

  setMode(mode) {
    this.grids.forEach((g) => { g.dataset.view = mode; });
    this.querySelectorAll('[data-view-mode]').forEach((btn) => {
      btn.setAttribute('aria-pressed', btn.dataset.viewMode === mode ? 'true' : 'false');
    });
  }
}
customElements.define('listing-controls', ListingControls);

/**/
/* The collection hero closes with a smooth swipe-up when*/
/* 2+ filters are active (the collection context itself + any filter*/
/* Param) and returns at one. Fresh loads with params start closed —*/
/* No entrance jump; live filter changes animate. Reduced motion*/
/* Snaps (the CSS carries the media query).*/
/**/
(() => {
  const hero = document.querySelector('[data-coll-hero]');
  if (!hero) return;
  const filtered = () =>
    [...new URLSearchParams(window.location.search).entries()].some(([k, v]) => k.startsWith('filter.') && v !== '');
  const apply = () => hero.classList.toggle('is-closed', filtered());
  if (filtered()) hero.classList.add('is-closed');
  requestAnimationFrame(() => {
    hero.style.setProperty('--coll-hero-max', `${Math.max(hero.scrollHeight, 200)}px`);
    hero.setAttribute('data-animatable', '');
  });
  document.addEventListener('dr:filters-applied', apply);
  window.addEventListener('popstate', () => setTimeout(apply, 50));
})();

/**/
/* Collection banner carousel: one image at a time,*/
/* White chevron arrows + dash indicators (active dash thicker), per*/
/* Collections.pdf. Track slides via translate; reduced-motion snaps.*/
/**/
class BannerCarousel extends HTMLElement {
  connectedCallback() {
    whenParsed(() => this.init());
  }

  init() {
    this.track = this.querySelector('[data-carousel-track]');
    this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
    this.dashes = Array.from(this.querySelectorAll('[data-carousel-dash]'));
    if (!this.track || this.slides.length < 2) return;
    this.index = 0;
    if (this.localBound) return;
    this.localBound = true;
    this.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-carousel-move]');
      if (!btn) return;
      this.go(this.index + Number(btn.dataset.carouselMove));
    });
  }

  go(next) {
    const count = this.slides.length;
    this.index = ((next % count) + count) % count;
    this.track.style.translate = `${this.index * -100}% 0`;
    this.dashes.forEach((d, i) => d.setAttribute('aria-current', i === this.index ? 'true' : 'false'));
    this.slides.forEach((s, i) => s.toggleAttribute('inert', i !== this.index));
  }
}
customElements.define('banner-carousel', BannerCarousel);

/**/
/* LOAD MORE. Fetches*/
/* The next paginated page onto the grid ON CLICK. Progressive: the*/
/* Numbered pagination nav is the no-JS fallback and is hidden by JS*/
/* Only once a next page is known to exist. Appended cells inherit the*/
/* Hairline grid nth-child rules and lazy images automatically.*/
/*                                                                     */
/* 🔴 THIS WAS INFINITE SCROLL UNTIL and the element keeps its*/
/* Tag name deliberately — `<infinite-listing>` is referenced by the*/
/* Browse By drawer's live re-filter (`reset(doc)` below), and renaming*/
/* It would mean touching that seam for no behavioural gain. Only the*/
/* TRIGGER changed: an IntersectionObserver sentinel became a button.*/
/* For a button; leaving the observer in place as well*/
/* Would have had two mechanisms racing to append the same page.*/
/* ⚠ Journal and Projects are NOT in scope — they keep numbered pages*/
/* And never carried this*/
/* Element at all.*/
/**/
class InfiniteListing extends HTMLElement {
  connectedCallback() {
    whenParsed(() => this.init());
  }

  init() {
    this.grid = document.querySelector('[data-listing-grid]');
    this.button = this.querySelector('[data-load-more]');
    this.status = this.querySelector('[data-load-status]');
    this.strings = {
      more: this.button?.textContent.trim() || 'Load more',
      status: this.dataset.statusTemplate || '{{ added }} more loaded. Showing {{ shown }} of {{ total }}.',
      done: this.dataset.doneTemplate || 'All {{ total }} shown.',
    };
    /* ⚠ Focus is driven by INTENT, not by which call happens to finish.
       A silent auto-advance can still be in flight when the visitor
       presses the button; that click then returns early on `this.loading`
       and, if focus were tied to the call, nobody would ever be focused.
       The flag records that a human asked, and whichever load completes
       last honours it.*/
    this.button?.addEventListener('click', () => { this.userRequested = true; this.loadNext(); });
    /* The price BANDS lean on Shopify's filter, whose range semantics the $0
       Custom Size variant poisons too — it matches EVERY band, so a product
       whose cheapest real variant is $11,990 passes "under $3,000" (measured:
       65 of 584 active products leak this way). The same doctrine tightens it
       here: a card whose cheapest REAL price is outside the active band is
       REMOVED; enquire-only (no real price) has no price to qualify and goes
       with them.
       🔴 REMOVED, NOT HIDDEN, AND THAT IS THE WHOLE FIX (01.09). Hiding left
       the rest of the page reasoning about cards nobody can see: SIX
       `:nth-child` hairline/separator rules across four breakpoints counted
       hidden cells (greg-natale's one surviving card drew a stray rule above
       it), the server-rendered empty state keys off `rendered_count` so it
       could never fire, and the top-up below measured a grid that was
       visually empty as full. Removal makes every one of those correct with
       no parallel "visible index" to maintain.
       ⚠ PARSED BEFORE arm() ON PURPOSE — arm() decides whether to top the
       page up, and that decision must count what SURVIVES the band, or a
       collection whose whole first page is over-band never refills.*/
    this.bandLte = (location.search.match(/filter\.v\.price\.lte=(\d+)/) || [])[1];
    if (this.bandLte) this.applyBand();
    this.arm(document.querySelector('[data-listing-pagination]'));
    /* 🔴 J4 — LOW–HIGH IS BROKEN NATIVELY AND THIS IS THE FIX.
       Root cause (proven on the rendered order): Shopify's
       ascending key is price_min, and permanent $0 Custom Size
       variant ties the ENTIRE catalogue at $0 — descending (price_max)
       is monotonic, ascending is arbitrary. No native key can skip the
       $0, so when sort_by=price-ascending is active the loader fetches
       every remaining server page (same nextUrl chain the button uses)
       and reorders the grid by each card's own data-card-price — the
       CHEAPEST REAL variant, the exact rule the card price already
       obeys. Enquire-only cards (0) sort last, ties keep server order
       (stable sort). this finishes client-side from
       location.search; the server render is untouched.*/
    if (/sort_by=price-ascending/.test(location.search)) this.fullSortAscending();
    /* The price BANDS lean on Shopify's filter, whose range
       semantics the $0 Custom Size variant partially poisons too
       (measured: a $13,590-from product inside the ≤$5,000 band). The
       same doctrine tightens it here: a card whose cheapest REAL
       price exceeds the active band hides; enquire-only (no real price)
       hides inside a price band as well. Runs on init and after every
       page load.*/
  }

  /* The grid's own cells, never `children` — the band's empty-state
     paragraph is a child too, and counting it as a card is how an empty
     grid reports itself as full.*/
  cells() {
    return [...(this.grid?.querySelectorAll(':scope > .listing-grid__cell') || [])];
  }

  applyBand() {
    if (!this.grid || !this.bandLte) return;
    const cap = parseInt(this.bandLte, 10) * 100;
    for (const cell of this.cells()) {
      const p = parseInt(cell.querySelector('[data-card-price]')?.dataset.cardPrice || '0', 10);
      if (!(p > 0 && p <= cap)) cell.remove();
    }
  }

  /* ⚠ The empty state is SERVER-rendered on `rendered_count == 0`, and the
     snippet's own comment already names this bug class — "a filtered page
     could have items and render nothing". The band is the client-side half
     of exactly that: the server rendered 19 cards and the band removed all
     19, so `rendered_count` was 19 and the message could never fire. The
     shopper got a blank grid under the heading with no explanation.
     ⚠ Never announce empty while a next page is still coming — auto-advance
     is mid-flight and cards may yet arrive.*/
  reconcileEmpty() {
    if (!this.grid || !this.bandLte) return;
    const existing = this.grid.querySelector('[data-band-empty]');
    if (this.cells().length || this.nextUrl) { existing?.remove(); return; }
    if (existing) return;
    const p = document.createElement('p');
    p.className = 'listing-grid__empty';
    p.setAttribute('data-band-empty', '');
    p.textContent = this.dataset.emptyText || 'No designs match your selection.';
    this.grid.appendChild(p);
  }

  async fullSortAscending() {
    if (!this.grid) return;
    let guard = 40; /* 40 pages ≫ the catalogue; a runaway stop, not a cap*/
    while (this.nextUrl && guard--) {
      const before = this.nextUrl;
      await this.loadNext();
      if (this.nextUrl === before) break; /* A failed fetch must not spin*/
    }
    const cells = this.cells();
    const price = (cell) => {
      const p = parseInt(cell.querySelector('[data-card-price]')?.dataset.cardPrice || '0', 10);
      return p > 0 ? p : Number.MAX_SAFE_INTEGER;
    };
    cells
      .map((cell, i) => ({ cell, i, p: price(cell) }))
      .sort((a, b) => a.p - b.p || a.i - b.i)
      .forEach(({ cell }) => this.grid.appendChild(cell));
    if (this.status) this.status.textContent = this.strings.done.replace('{{ total }}', String(this.cells().length));
    if (this.button) this.button.hidden = true;
  }

  /* (Re)arm against a pagination nav — init and the live re-filter
     share this: the drawer hands over the freshly
     fetched doc and scrolling continues against the new filter set.*/
  arm(nav) {
    this.nav = nav;
    this.autoAdvances = 0;
    this.nextUrl = nav?.querySelector('[data-next-page]')?.getAttribute('href') || null;
    if (this.button) this.button.hidden = !this.nextUrl;
    this.minCards = Number(this.dataset.minCards || 0);
    if (!this.grid || !this.nextUrl) { this.reconcileEmpty(); return; }
    /* 🔴 A PAGE CAN RENDER ZERO CARDS, AND SHOP ALL CURRENTLY DOES.
       `paginate` counts collection.products, but the grid EXCLUDES
       combined-listing parents, `grid-hidden` construction siblings and
       auction lots at render time. On /collections/all-rugs — sorted
       best-selling — the first 24 in that order are all excluded, so page
       one paints an EMPTY grid while the pager still says 548.
       ⚠ This PRE-DATES load more (numbered pagination hid it: you clicked
       to page 2 and saw products) and it is ORDER-DEPENDENT, which is why
       it comes and goes as best-selling is recomputed. THE REAL FIX IS
       DATA — collection membership or sort order — and is reported, not
       guessed at here.
       🔴 — RAISED FROM "empty" TO "below a full page".
       is that a full page is 24 cards. The page size is now Liquid's maximum
       of 50, which is the most the platform can be asked for in one request,
       and on /collections/all-rugs that still yields only NINETEEN cards
       because ~31 excluded products sit at the front of best-selling order.
       So when the first render comes up short, the loader tops it up before
       the visitor sees the page. Bounded to 3 fetches so a pathological
       collection cannot spin, silent (no announcement, no focus steal), and
       it never runs on a collection that is simply small — `nextUrl` is null
       there, which is why akira-isogawa's 5 and oval-rugs' 4 are left alone.
       ⚠ THIS IS A JS-ONLY TOP-UP. With JS off, all-rugs page one still
       renders 19; only the DATA fix closes that last gap. Reported, not
       hidden.*/
    if (this.cells().length < this.minCards) this.autoAdvance();
    /* ⚠ The numbered nav is hidden HERE, by JS, and only once we know a
       next page exists. With JS off it never runs, so the server-rendered
       pagination — real <a href> links — stays as the fallback and as
       the crawlable path to deep products.*/
    this.nav.hidden = true;
  }

  reset(doc) {
    const freshNav = doc.querySelector('[data-listing-pagination]');
    let nav = document.querySelector('[data-listing-pagination]');
    if (nav && freshNav) {
      const adopted = document.adoptNode(freshNav);
      nav.replaceWith(adopted);
      nav = adopted;
    } else if (nav && !freshNav) {
      nav.remove();
      nav = null;
    } else if (!nav && freshNav) {
      const adopted = document.adoptNode(freshNav);
      this.grid?.after(adopted);
      nav = adopted;
    }
    this.loading = false;
    this.arm(nav);
  }

  async autoAdvance() {
    while (this.cells().length < this.minCards && this.nextUrl && this.autoAdvances < 3) {
      this.autoAdvances += 1;
      await this.loadNext({ silent: true });
    }
  }

  say(template, vars) {
    if (!this.status) return;
    this.status.textContent = Object.entries(vars)
      .reduce((t, [k, v]) => t.replace(new RegExp(`\\{\\{\\s*${k}\\s*\\}\\}`, 'g'), v), template);
  }

  async loadNext({ silent = false } = {}) {
    if (this.loading || !this.nextUrl) return;
    this.loading = true;
    this.setAttribute('aria-busy', 'true');
    if (this.button) { this.button.disabled = true; }
    try {
      const res = await fetch(this.nextUrl);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const cells = doc.querySelectorAll('[data-listing-grid] > .listing-grid__cell');
      const added = cells.length;
      cells.forEach((c) => this.grid.appendChild(document.adoptNode(c)));
      document.dispatchEvent(new CustomEvent('dr:listing-appended'));
      this.nextUrl = doc.querySelector('[data-listing-pagination] [data-next-page]')?.getAttribute('href') || null;
      const shown = this.cells().length;
      const total = Number(this.dataset.total || 0) || shown;
      /* ⚠ ACCESSIBILITY, and this is where load-more usually fails focus STAYS on the button so a keyboard user can press it again
         without hunting, and the addition is announced through an
         aria-live region instead. Moving focus into the grid would strand
         someone who wanted the next page.*/
      if (this.nextUrl) {
        if (!silent) this.say(this.strings.status, { added, shown, total });
      } else {
        if (!silent) this.say(this.strings.done, { added, shown, total });
        /* ⚠ never leave a dead control: the button goes, it does not sit
           there disabled and unexplained*/
        if (this.button) this.button.hidden = true;
      }
    } catch (e) {
      /* Network hiccup: put the real pagination back as the manual route*/
      if (this.nav) this.nav.hidden = false;
      if (this.button) this.button.hidden = true;
    } finally {
      this.loading = false;
      this.removeAttribute('aria-busy');
      if (this.bandLte) { this.applyBand(); this.reconcileEmpty(); } /* Tighten every page*/
      if (this.button) {
        /* 🔴 ORDER MATTERS AND I GOT IT WRONG FIRST: re-enable BEFORE
           focusing. A DISABLED BUTTON CANNOT RECEIVE FOCUS, so calling
           focuswhile `disabled` was still true silently did nothing and
           activeElement fell back to <body> — exactly what the suite
           reported, and it looked like the fix had not deployed.
           ⚠ Only refocus while the button is still there; once everything
           is loaded it is hidden, and focusing a hidden control strands
           the keyboard user. `preventScroll` because proved focuswill scroll a clipping container.*/
        this.button.disabled = false;
        /* Don't steal focus before the visitor has pressed anything*/
        if (this.userRequested && !this.button.hidden) {
          this.button.focus({ preventScroll: true });
          this.userRequested = false;
        }
      }
    }
  }
}
customElements.define('infinite-listing', InfiniteListing);

/**/
/* Predictive search: the announcement-bar search icon*/
/* The announcement-bar country selector submits its own
   native Markets form on change. Progressive enhancement: the form has a
   noscript submit button, so this only removes a click. It renders at all
   only when localization.available_countries.size > 1, so on a single-market
   store there is nothing here to bind.*/
document.addEventListener('change', (event) => {
  const sel = event.target.closest?.('[data-region-select]');
  if (sel && sel.form) sel.form.submit();
});

/* Opens an inline panel; suggestions via /search/suggest.json*/
/* (products + collections + articles — the native predictive API).*/
/* Progressive: without JS the icon stays a link to /search.*/
/**/
/**/
/* Search fly-down: full-width in-flow bar under the header —*/
/* Opening pushes the page down (grid-rows reveal, no overlay). The*/
/* Trigger lives in the announcement bar as a plain /search link*/
/* (no-JS fallback); segmented predictive results build beneath.*/
/**/

/* 🔴 210 (02.09) — ONE CHILD PER COMBINED LISTING, THE WAY THE THEME DECIDES IT.
   suggest.json and collections/*.json carry tags but no metafields, so this
   rail cannot read the parent's `custom.default_child` on its own. For any
   listing that appears more than once in a result set it asks the theme's
   resolver — sections/default-child-json via the Section Rendering API, ONE
   small cached request per listing — and keeps the child the resolver names:
   the parent's override, else the hand-knotted child. Only when the resolver
   names nothing (no construction axis) does the old tag preference apply:
   the child that is not `grid-hidden`, else the first.
   ⚠ The listing is identified by the HANDLE STEM, not the `design:` tag:
   three separate listings share `design:tiger`.
   ⚠ A lone grid-hidden item that is NOT a construction child (no suffix to
   strip) is dropped, as before: five auction demo lots were surfaced by the
   flyout that way once. */
const dcStem = (h) => (h || '').replace(/-(hand-?knotted|hand-?tuft(ed)?|hand-?loomed)$/i, '');
const dcCache = new Map();
async function resolveDefaultChild(anyHandle) {
  const key = dcStem(anyHandle);
  if (dcCache.has(key)) return dcCache.get(key);
  const pending = fetch(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(anyHandle)}?section_id=default-child-json`)
    .then((r) => (r.ok ? r.text() : ''))
    .then((html) => { const m = html.match(/\{"default":\s*"([^"]*)"/); return m ? m[1] : ''; })
    .catch(() => '');
  dcCache.set(key, pending);
  return pending;
}
async function oneChildPerListing(products) {
  const groups = new Map();
  for (const p of products) {
    const tags = (p.tags || []).map((t) => String(t).toLowerCase());
    if (tags.includes('cl-parent') || tags.includes('on_auction') || tags.includes('auction-demo')) continue;
    const hidden = tags.includes('grid-hidden');
    if (hidden && dcStem(p.handle) === p.handle) continue;
    const key = dcStem(p.handle);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push({ p, hidden });
  }
  const out = [];
  for (const [, members] of groups) {
    if (members.length === 1) { out.push(members[0].p); continue; }
    const want = await resolveDefaultChild(members[0].p.handle);
    const byResolver = want && members.find((m) => m.p.handle === want);
    if (byResolver) { out.push(byResolver.p); continue; }
    const visible = members.find((m) => !m.hidden) || members[0];
    out.push(visible.p);
  }
  return out;
}

class SearchFlyout extends HTMLElement {
  connectedCallback() {
    whenParsed(() => this.init());
  }

  init() {
    this.input = this.querySelector('[data-search-input]');
    this.results = this.querySelector('[data-search-results]');
    if (!this.input) return;

    this.onTriggerClick = this.onTriggerClick || ((event) => {
      const trigger = event.target.closest('[data-search-open]');
      if (!trigger) return;
      event.preventDefault();
      if (this.classList.contains('is-open')) this.close();
      else this.open(trigger);
    });
    document.addEventListener('click', this.onTriggerClick);

    this.onOutsideClick = this.onOutsideClick || ((event) => {
      if (!this.classList.contains('is-open')) return;
      if (this.contains(event.target) || event.target.closest('[data-search-open]')) return;
      this.close(false);
    });
    document.addEventListener('click', this.onOutsideClick);

    // theme editor: selecting the section shows the open state
    this.onSectionSelect = this.onSectionSelect || ((event) => {
      if (event.target.contains(this)) this.open(null);
    });
    document.addEventListener('shopify:section:select', this.onSectionSelect);

    if (!this.localBound) {
      this.localBound = true;
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-flyout-close]')) this.close();
      });
      this.addEventListener('keyup', (event) => {
        if (event.code === 'Escape') this.close();
      });
      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.suggest(), 250);
      });
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onTriggerClick);
    document.removeEventListener('click', this.onOutsideClick);
    document.removeEventListener('shopify:section:select', this.onSectionSelect);
    /* The element can go while open (theme editor re-renders the section);
       leaving the flag on <html> would strand every hero at offset 0*/
    document.documentElement.removeAttribute('data-search-flyout-open');
  }

  open(trigger) {
    this.activeTrigger = trigger || this.activeTrigger;
    this.classList.add('is-open');
    /* Publish the open state on <html>, the same way the scroll
       watcher publishes data-chrome-hidden. The under-header heroes rise
       by a full header height (see .hero-under-header in base.css); one
       CSS rule keyed off this attribute zeroes that offset while the bar
       occupies the space, so no hero needs to know the flyout exists.
       ⚠ NAMED -flyout- ON PURPOSE: `[data-search-open]` is already the
       selector for the trigger BUTTONS one line below, so reusing it here
       would make <html> match that query and take an aria-expanded.*/
    document.documentElement.setAttribute('data-search-flyout-open', '');
    document.querySelectorAll('[data-search-open]').forEach((t) => t.setAttribute('aria-expanded', 'true'));

    /* ONE OVERLAY AT A TIME. The Browse By
       drawer's scrim is fixed at z-index 38 and the flyout sits at 30, so
       with the drawer open the search bar opened *behind* it and search
       appeared to do nothing. Closing the drawer here matches the cart
       drawer and the mobile menu.
       ⚠ THIS DOES NOT WEAKEN 's RULE that the drawer stays open on
       selection and only the user closes it. That rule exists so a filter
       click never dismisses the tool mid-task. Opening a different tool is
       a deliberate user action — the same category as ×, an outside click
       or ESC — so it counts as the user closing it. Do not read this as a
       regression of the keep-open behaviour.
       ⚠ Order matters: the drawer's closerestores focus to its own
       trigger and releases the scroll lock, so it must run BEFORE this
       method focuses the search input, or focus lands on the wrong control.
       Filter state is untouched — filters live in the URL and the applied
       chips are rendered from it, so closing the drawer cannot reset them.*/
    document.querySelectorAll('browse-by-drawer').forEach((d) => {
      if (document.documentElement.dataset.drBrowsebyOpen !== undefined) d.close?.();
    });
    // An in-flow push is only visible at the top — glide back up first.
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (window.scrollY > 0) {
      const lenis = window.DRMotion?.lenis;
      if (lenis && !reduced) lenis.scrollTo(0);
      else window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
    }
    this.input.focus({ preventScroll: true });
  }

  close(refocus = true) {
    this.classList.remove('is-open');
    document.documentElement.removeAttribute('data-search-flyout-open');
    document.querySelectorAll('[data-search-open]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
    if (refocus) (this.activeTrigger || document.querySelector('[data-search-open]'))?.focus();
  }

  async suggest() {
    const q = this.input.value.trim();
    if (q.length < 2) {
      this.results.innerHTML = '';
      return;
    }
    const limit = Number(this.dataset.limit) || 6;
    try {
      // unavailable_products=last, not hide: most DR designs carry no
      // inventory (made-to-order / enquiry) and read as "unavailable" —
      // hiding them would empty the Designs segment on this catalogue.
      const url = `${window.Shopify?.routes?.root || '/'}search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product,collection,article&resources[limit]=${limit}&resources[options][unavailable_products]=last`;
      const res = await fetch(url);
      const data = await res.json();
      if (this.input.value.trim() !== q) return; // stale response
      const r = data.resources.results;
      /* 🔴 ONE ENTRY PER DESIGN IN THE FLYOUT.
         "when I search Tiger Knot… it's coming up twice."

         ⚠ THE FLYOUT AND THE RESULTS PAGE ARE DIFFERENT SYSTEMS, and only
         the flyout was wrong. The results page is our Liquid, so's
         `cl-parent` exclusion applies there; measured on "tiger knot" it
         returns 11 cards with no duplicate design. The flyout reads
         Shopify's /search/suggest.json — an endpoint we do not render — so
         no theme-side tag rule had ever reached it.

         ⚠ AND DIAGNOSIS WAS WRONG, which matters because it points
         at the wrong fix: he said "that's the parent and the child". It is
         not. Measured, the duplicates are the two CONSTRUCTION CHILDREN —
         `tiger-knot-jade` and `tiger-knot-jade-hand-knotted`, both titled
         "Tiger Knot Jade". No parent appears in the payload at all.

         The payload carries `tags`, so the same signals the listing grid
         uses are available here: drop `cl-parent`, then keep ONE child per
         combined listing, preferring the one that is not `grid-hidden` —
         and if every child of a listing is hidden, keep the first, which is
         the orphan rule the grid follows.

         ⚠ The listing is identified by the HANDLE STEM, not the `design:`
         tag: three separate listings share `design:tiger`, so a design-tag
         key would collapse Caramel, Chilli and Jade into one result.
         ⚠ invariant is preserved by construction — this only ever
         REMOVES entries from the flyout, so anything it still surfaces is
         still on the results page.*/
      if (Array.isArray(r.products)) {
        /* 210 (02.09): ONE helper for both rails, and it asks the theme's
           resolver which child is the default (parent override → hand-knotted
           → the grid-hidden tag) instead of keeping its own tag rule. */
        r.products = await oneChildPerListing(r.products);
      }
      /* 🔴 J1 (client screenshot, ) — A COLLECTION-TITLE MATCH
         SURFACES THAT COLLECTION'S PRODUCTS, ranked above prose matches.
         proved the mechanism gap: nothing searchable on the 8 Alchemy
         rugs says "alchemy" (native search indexes neither membership nor
         custom.collection), while unrelated products match on prose.
         MATCHING RULE, deliberately tight: the collection must come back
         from suggest.json (Shopify matched it) AND the query (≥3 chars)
         must appear in its TITLE — a description-only match never hijacks
         the rail. Members come from /collections/{handle}/products.json in
         the collection's own order, then pass the SAME rules as everything
         in this rail: cl-parent dropped, one child per listing via the
         handle stem, grid-hidden losing to its visible sibling.
         Prose matches stay, deduped by stem, ranked after. Works with no
         data change; range-name tags remain a useful reinforcement.*/
      try {
        const qlc = q.toLowerCase();
        const titleMatch = qlc.length >= 3
          ? ((r.collections || []).find((c) => (c.title || '').toLowerCase() === qlc) || (r.collections || []).find((c) => (c.title || '').toLowerCase().includes(qlc)))
          : null;
        if (titleMatch && Array.isArray(r.products)) {
          const cres = await fetch(`${window.Shopify?.routes?.root || '/'}collections/${titleMatch.handle}/products.json?limit=24`);
          if (cres.ok) {
            const cdata = await cres.json();
            if (this.input.value.trim() !== q) return; /* Stale*/
            /* 210: the same helper as the prose rail — the collection band
               used to carry its own copy of the tag rule; now there is one. */
            const members = (await oneChildPerListing(cdata.products || [])).map((p) => {
              const img = p.images?.[0]?.src || '';
              return {
                url: `${window.Shopify?.routes?.root || '/'}products/${p.handle}`,
                title: p.title,
                handle: p.handle,
                featured_image: img ? { url: img } : null,
              };
            });
            const seenStems = new Set(members.map((m) => dcStem(m.handle)));
            const prose = r.products.filter((p) => !seenStems.has(dcStem(p.handle)));
            const exactCollection = titleMatch.title.toLowerCase() === qlc;
            r.products = (exactCollection && members.length ? members : members.concat(prose)).slice(0, Math.max(limit, 8));
          }
        }
      } catch (e) {
        /* Quiet — the plain suggest results still render*/
      }
      const group = (label, items, cls, render) =>
        items?.length
          ? `<div class="sflyout__group"><p class="sflyout__group-label">${label}</p><div class="sflyout__items ${cls}">${items.map(render).join('')}</div></div>`
          : '';
      const designs = group(this.dataset.labelProducts || 'Designs', r.products, 'sflyout__items--designs', (p) => `
        <a class="sflyout__item sflyout__item--design" href="${p.url}">
          ${p.featured_image?.url ? `<img src="${p.featured_image.url}${p.featured_image.url.includes('?') ? '&' : '?'}width=320" alt="" loading="lazy">` : ''}
          <span>${p.title}</span>
        </a>`);
      const colls = group(this.dataset.labelCollections || 'Collections', r.collections, '', (c) => `<a class="sflyout__item" href="${c.url}"><span>${c.title}</span></a>`);
      /* The fallback must match the locale string, not invent a THIRD
         wording. This said 'Posts / News' while the results page said 'Posts
         and News' and the two blogs are called Journal and Projects, which is
         exactly why case studies read as missing: nothing on screen used the
         site's own names for them. One wording everywhere: 'Journal &
         Projects'.*/
      const posts = group(this.dataset.labelPosts || 'Journal & Projects', r.articles, '', (a) => `<a class="sflyout__item" href="${a.url}"><span>${a.title}</span></a>`);
      /* 🔴 NO `type` param, for the same reason
         the flyout's form no longer posts one: `&type=product` on the
         results URL narrowed the page to products and beat widening
         of the results page's own form. "View all results" that quietly
         drops every post is the opposite of what the link says.*/
      const viewAll = `<a class="sflyout__all" href="${window.Shopify?.routes?.root || '/'}search?q=${encodeURIComponent(q)}">${this.dataset.viewAllLabel || 'View all results'}</a>`;
      this.results.innerHTML =
        designs || colls || posts
          ? `${designs}${colls}${posts ? posts : ''}`.concat(`<div class="sflyout__group">${viewAll}</div>`)
          : `<p class="sflyout__none">${(this.dataset.noResults || 'No results for “[q]”').replace('[q]', q.replace(/[<>&"]/g, ''))}</p>${viewAll}`;
    } catch (e) {
      /* Quiet — the form submit still works*/
    }
  }
}
customElements.define('search-flyout', SearchFlyout);

/**/
/* Collections Hub kind tabs: All · Studio ·*/
/* Collaborations, URL-reflected (?kind=) and shareable. Progressive*/
/* Enhancement — the tab hrefs carry the param + an #anchor, so no-JS*/
/* Lands on the group; JS upgrades to instant toggle + replaceState.*/
/**/
class CollectionsHub extends HTMLElement {
  connectedCallback() {
    whenParsed(() => this.init());
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;
    this.tabs = Array.from(this.querySelectorAll('[data-kind]'));
    this.groups = Array.from(this.querySelectorAll('[data-hub-group]'));
    if (!this.tabs.length) return;
    const param = new URLSearchParams(window.location.search).get('kind');
    this.apply(param || 'all', false);
    this.addEventListener('click', (event) => {
      const tab = event.target.closest('[data-kind]');
      if (!tab) return;
      event.preventDefault();
      this.apply(tab.dataset.kind, true);
    });
  }

  apply(kind, push) {
    if (!['all', 'studio', 'collaborations'].includes(kind)) kind = 'all';
    this.tabs.forEach((t) => t.setAttribute('aria-current', String(t.dataset.kind === kind)));
    this.groups.forEach((g) => {
      g.hidden = kind !== 'all' && g.dataset.hubGroup !== kind;
    });
    if (push) {
      const url = new URL(window.location);
      if (kind === 'all') url.searchParams.delete('kind');
      else url.searchParams.set('kind', kind);
      url.hash = '';
      history.replaceState({}, '', url);
    }
  }
}
customElements.define('collections-hub', CollectionsHub);

/**/
/* Side sheets: Book a*/
/* Consultation (Calendly lazy-loads on open), Make an Enquiry and the*/
/* Trade panel on the PDP, plus main-article's enquire panel.*/
/* Focus-trapped, ESC/backdrop close. product.js instantiates it on*/
/* <product-page>; elsewhere wrap the region in <side-sheet-host>.*/
/**/
class SideSheets {
  constructor(root) {
    this.root = root;
    this.overlay = root.querySelector('[data-sheet-overlay]');
    root.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-sheet-open]');
      if (opener) this.open(opener.dataset.sheetOpen, opener);
      if (event.target.closest('[data-sheet-close]') || event.target === this.overlay) this.close();
    });
    root.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') this.close();
    });
    root.querySelector('[data-consult-route]')?.addEventListener('change', (event) => {
      root.querySelectorAll('[data-consult-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.consultPanel !== event.target.value;
      });
      if (event.target.value === 'calendly') this.loadCalendly();
    });
  }

  open(name, trigger) {
    this.trigger = trigger;
    this.active = this.root.querySelector(`[data-sheet="${name}"]`);
    if (!this.active) return;
    /* The opening button governs the sheet title (client
       the Pre-order button retitles the enquiry sheet to
       "Pre-order This Piece"; same form underneath). The default title
       is banked once so a later plain open restores it.*/
    const title = this.active.querySelector('.side-sheet__title');
    if (title) {
      if (title.dataset.defaultTitle === undefined) title.dataset.defaultTitle = title.textContent;
      title.textContent = (trigger && trigger.dataset.sheetTitle) || title.dataset.defaultTitle;
    }
    /* 🔴 AND THE ENQUIRY IDENTIFIES ITSELF. One sheet now serves the piece
       enquiry, the custom-size enquiry and the pre-order swap, so the heading
       alone would leave the merchant's inbox unable to tell them apart. The
       opening button carries the type and writes it into the form, the same
       way it already carries the title. Banked and restored like the title, so
       a plain open cannot leave the previous trigger's value behind. */
    const typeField = this.active.querySelector('[data-enquiry-type-field]');
    if (typeField) {
      if (typeField.dataset.defaultType === undefined) typeField.dataset.defaultType = typeField.value;
      typeField.value = (trigger && trigger.dataset.enquiryType) || typeField.dataset.defaultType;
    }
    /* 225 — AND THE FORM IDENTIFIES ITSELF THE SAME WAY. One sheet serves the
       piece enquiry AND the pre-order (the OOS button re-titles it), so the two
       must not report as one form to the agency's container. The opening button
       may override data-form-id / data-form-name exactly as it already
       overrides the title and the enquiry type — banked and restored, so a
       plain open can never leave the previous trigger's identity behind. */
    const idForm = this.active.querySelector('[data-app-placement][data-form-id], form[data-form-id]');
    if (idForm) {
      if (idForm.dataset.defaultFormId === undefined) {
        idForm.dataset.defaultFormId = idForm.dataset.formId;
        idForm.dataset.defaultFormName = idForm.dataset.formName || '';
      }
      idForm.dataset.formId = (trigger && trigger.dataset.formId) || idForm.dataset.defaultFormId;
      idForm.dataset.formName = (trigger && trigger.dataset.formName) || idForm.dataset.defaultFormName;
      if (idForm.matches('[data-native-retail-app]')) {
        idForm.dataset.nativePendingEnquiryType = trigger?.dataset.enquiryType || '';
      }
      window.DRNativeRetailForms?.sync(idForm);
    }
    if (this.overlay) {
      this.overlay.hidden = false;
      // the trade panel draws no scrim (comp/browse-by pattern) — the
      // overlay stays for outside-click close, just transparent
      this.overlay.classList.toggle('side-sheet__overlay--clear', name === 'trade-enquiry');
    }
    this.active.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    window.trapFocus(this.active, this.active.querySelector('[data-sheet-close]'));
  }

  close() {
    if (!this.active) return;
    this.active.hidden = true;
    if (this.overlay) this.overlay.hidden = true;
    document.documentElement.style.overflow = '';
    window.removeTrapFocus(this.trigger || null);
    this.active = null;
  }

  loadCalendly() {
    const holder = this.root.querySelector('[data-calendly-holder]');
    if (!holder || holder.dataset.loaded) return;
    holder.dataset.loaded = 'true';
    const url = new URL(holder.dataset.calendlyUrl);
    url.searchParams.set('embed_domain', window.location.hostname);
    url.searchParams.set('embed_type', 'Inline');
    holder.innerHTML = `<iframe src="${url}" title="Calendly" loading="lazy"></iframe>`;
  }
}

class SideSheetHost extends HTMLElement {
  connectedCallback() {
    whenParsed(() => {
      if (!this.sheets) this.sheets = new SideSheets(this);
    });
  }
}
customElements.define('side-sheet-host', SideSheetHost);

/* 🔴 214 J8 — `href="#calendly"` OPENS THE SHARED CALENDLY LIGHTBOX, FROM ANYWHERE.
   The one instruction for Chris and Ammie: set a button's link to #calendly.
   layout/theme.liquid renders one buttonless lightbox instance
   ([data-calendly-lightbox]) whenever settings.calendly_url is set; this
   delegate catches every click on such a link — CMS buttons, menu rows,
   footer items, links inside sections loaded later — and opens it. On load
   (and after shopify:section:load) the anchors are rewritten to the real
   Calendly URL with a data marker, so middle-click, "open in new tab" and
   hover all show a real destination; the marker keeps the delegate in play.
   Where no instance exists (URL unset) the link falls through to the URL it
   was rewritten to, or stays an inert anchor with JS off. */
const CALENDLY_MATCH = (a) => a && (a.dataset.calendly === '1' || /(^|\/)#calendly$/.test(a.getAttribute('href') || ''));
function calendlyLightbox() { return document.querySelector('[data-calendly-lightbox]'); }
function calendlyRewrite(root = document) {
  const lb = calendlyLightbox();
  const url = lb ? lb.dataset.url : '';
  root.querySelectorAll('a[href="#calendly"], a[href$="/#calendly"]').forEach((a) => {
    a.dataset.calendly = '1';
    if (url) { a.setAttribute('href', url); a.setAttribute('target', '_blank'); a.setAttribute('rel', 'noopener'); }
  });
}
document.addEventListener('click', (event) => {
  const a = event.target.closest('a');
  if (!CALENDLY_MATCH(a)) return;
  const lb = calendlyLightbox();
  if (!lb || typeof lb.open !== 'function') return; /* no instance: the rewritten href takes over */
  event.preventDefault();
  lb.open(a);
});
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => calendlyRewrite());
else calendlyRewrite();
document.addEventListener('shopify:section:load', (event) => calendlyRewrite(event.target || document));
